import { LaizeProps } from '../Components/Laizes/Laize';
import { MapContextState, MapPanelsState } from '../Context/MapContext';
import { MapItemContants } from '../Models/IMapItem';
import { IOpening } from '../Models/IOpening';
import { IRoom, RoomType } from '../Models/IRoom';
import { IRoomItem } from '../Models/IRoomItem';
import { Editor } from '../Utils/Editor';
import { TopologyException } from '../Utils/JstsUtils';
import { OpeningUtils } from '../Utils/Opening/OpeningUtils';
import { RoomDiffUtils } from '../Utils/Room/RoomDiffUtils';
import { RoomUtils } from '../Utils/Room/RoomUtils';
import { RoomItemUtils } from '../Utils/RoomItem/RoomItemUtils';
import { CoordPoint, LockedWall, Wall } from '../Utils/Types';
import { WallUtils } from '../Utils/Wall/WallUtils';
import { RoomFactory } from './BaseRoomFactory';
import { CompleteHelper } from './CompleteHelper';
import { MapResult } from './MapResult';
import { Logger } from '../../../Errors/Logger';
import { LogLevelType } from '../../../Services/Analytics/LogLevelType';
import { I18n } from '../../../Locales/I18nService';
import { Toast } from '../../../Web/Services/ToastService';

type ImportRoomsParams = {
    oldWalls?: Array<Wall>;
    oldRooms?: Array<IRoom>;
    openings?: Array<IOpening>;
    roomItems?: Array<IRoomItem>;
    importRooms: Array<IRoom>;
};

type LoadMapParams = {
    rooms: Array<IRoom>;
    laizeProps: Array<LaizeProps>;
    lockedWallsList?: Array<LockedWall>;
    flooringWallStartIndexes?: Record<string, number>;
};

export interface MapError {
    isMapError: boolean;
    error: any;
}

export class MapHelper {
    public static PANEL_WIDTH = 376;

    public static LOAD_MAP_ERROR_MESSAGE = 'LOAD_MAP_ERROR_MESSAGE';

    public static isPanelOpen = (state: MapPanelsState) =>
        Boolean(
            state.selectRoomShapeOpen ||
            state.selectAmenagementOpen ||
            state.selectStairsOpen ||
            state.selectedRoomId ||
            state.selectedOpeningId ||
            state.selectedRoomItemId ||
            state.showFinDeParcours ||
            state.showHabitation ||
            state.showStoreLocator
        );

    public static getPanelWidth = (state: MapPanelsState, parentWidth: number) => {
        if (state.showFinDeParcours || state.showHabitation || state.showStoreLocator) {
            return parentWidth / 2;
        }
        return MapHelper.PANEL_WIDTH;
    };

    //#region   //* LOAD MAP FROM API + CALC LAIZE RESULT

    public static loadRooms = ({
        rooms,
        laizeProps,
        lockedWallsList,
        flooringWallStartIndexes,
    }: LoadMapParams): Partial<MapContextState> => {
        try {
            return MapHelper.loadRoomsInternal({ rooms, laizeProps, lockedWallsList, flooringWallStartIndexes });
        } catch (error) {
            if ((error as TopologyException).name === 'TopologyException') {
                const topologyException = error as TopologyException;
                Logger.logAnalytics({
                    message: 'TopologyException error during map load, retrying with removed wall point',
                    level: LogLevelType.Error,
                    value: topologyException.pt,
                });

                const fixedRooms = MapHelper.removeWallPoint(rooms, topologyException.pt);
                const result = MapHelper.loadRoomsInternal({
                    rooms: fixedRooms,
                    laizeProps,
                    lockedWallsList,
                    flooringWallStartIndexes,
                });
                Toast.showInfo({
                    title: I18n.get('Toast_TitleInfo'),
                    content:
                        'Mur incompatible détecté sur le plan, nous avons appliqué une correction automatique, merci de vérifier le résultat',
                    duration: 5000,
                });
                return result;
            }
            const mapError: MapError = { isMapError: true, error };
            throw mapError;
        }
    };

    private static loadRoomsInternal = ({
        rooms,
        laizeProps,
        lockedWallsList,
        flooringWallStartIndexes,
    }: LoadMapParams): Partial<MapContextState> => {
        let updatedWalls: Array<Wall> = [];
        let updatedRooms: Array<IRoom> = [];
        let updatedOpenings: Array<IOpening> = [];
        let updatedRoomItems: Array<IRoomItem> = [];

        rooms.forEach((baseRoom) => {
            const { room, roomWalls = [] } = RoomUtils.createRoom(baseRoom, updatedWalls);
            updatedWalls = [...updatedWalls, ...roomWalls];
            room.walls = roomWalls;
            room.flooringWallStart = roomWalls[flooringWallStartIndexes?.[room.roomId!] || 0];

            updatedRooms = [...updatedRooms, room];
            Editor.architect(updatedWalls, updatedRooms);

            //! fix opening position from location_position_from_edge (occurs when duplicate room or import room)
            OpeningUtils.fixPositionOpenings(room, room.openings, updatedWalls);

            updatedOpenings = [...updatedOpenings, ...(room.openings || [])];
            updatedRoomItems = [...updatedRoomItems, ...(room.roomItems || [])];
            WallUtils.refreshWallsMinimumSizeAndOriginalAngle(updatedWalls, updatedOpenings);
        });

        updatedRooms.forEach(
            (item) =>
                (item.questionsCopiedFromId = item.questionsCopiedFromId || MapItemContants.QuestionsCopiedFromId_NONE)
        );
        updatedOpenings.forEach(
            (item) =>
                (item.questionsCopiedFromId = item.questionsCopiedFromId || MapItemContants.QuestionsCopiedFromId_NONE)
        );
        updatedRoomItems.forEach(
            (item) =>
                (item.questionsCopiedFromId = item.questionsCopiedFromId || MapItemContants.QuestionsCopiedFromId_NONE)
        );

        if (!updatedRooms.length) {
            return { rooms: [] };
        } else {
            updatedOpenings = OpeningUtils.resetOpeningsPlacement(updatedOpenings, updatedWalls);
            Editor.architect(updatedWalls, updatedRooms);
            updatedRooms
                .filter((r) => [RoomType.Sol, RoomType.Wall].includes(r.type!))
                .forEach((room) => {
                    OpeningUtils.updateRoomOpenings(room, updatedOpenings);
                    RoomItemUtils.updateRoomItems(room, updatedRoomItems);
                    RoomDiffUtils.roomComputeDifference(room, updatedRoomItems, updatedWalls);
                });
            const state = MapResult.calculate({
                rooms: updatedRooms,
                roomItems: updatedRoomItems,
                walls: updatedWalls,
                openings: updatedOpenings,
                laizeProps,
                logs: 'called by loadRooms',
            });
            const roomsCompleted = CompleteHelper.allComplete(rooms);
            const mapState: Partial<MapContextState> = {
                ...state,
                laizeProps,
                roomsCompleted,
                addRoomTutorialShown: true,
                lockedWallsList: lockedWallsList || [],
            };
            return mapState;
        }
    };

    /**
     * Remove a wall point matching the TopologyException coordinate from all rooms.
     * When a point is removed, the adjacent walls are merged (previous wall's end connects to next wall's start).
     * Related to #54464
     */
    public static removeWallPoint = (rooms: Array<IRoom>, pt: CoordPoint): Array<IRoom> => {
        return rooms.map((room) => {
            if (!room.coords || room.coords.length <= 3) {
                return room;
            }
            const filteredCoords = room.coords.filter(
                (coord) => !(Math.abs(coord.x - pt.x) < 1 && Math.abs(coord.y - pt.y) < 1)
            );
            if (filteredCoords.length < room.coords.length) {
                return { ...room, coords: filteredCoords };
            }
            return room;
        });
    };

    //#endregion

    //#region   //*USED ONLY IN LaizeV2DebugDialog

    public static importRooms = ({
        oldRooms = [],
        oldWalls = [],
        openings = [],
        roomItems = [],
        importRooms = [],
    }: ImportRoomsParams) => {
        let updatedWalls = oldWalls;
        let updatedRooms = oldRooms;
        let updatedOpenings = openings;
        let updatedRoomItems = roomItems;

        importRooms.forEach((baseRoom) => {
            baseRoom.name = baseRoom.name || RoomFactory.defaultName(updatedRooms.length);

            const { room, roomWalls = [] } = RoomUtils.createRoom(baseRoom, updatedWalls);
            updatedWalls = [...updatedWalls, ...roomWalls];
            room.walls = roomWalls;

            updatedRooms = [...updatedRooms, room];
            Editor.architect(updatedWalls, updatedRooms);

            //! fix opening position from location_position_from_edge (occurs when duplicate room or import room)
            OpeningUtils.fixPositionOpenings(room, room.openings, updatedWalls);

            updatedOpenings = [...updatedOpenings, ...(room.openings || [])];
            updatedRoomItems = [...updatedRoomItems, ...(room.roomItems || [])];
            WallUtils.refreshWallsMinimumSizeAndOriginalAngle(updatedWalls, updatedOpenings);
        });

        Editor.architect(updatedWalls, updatedRooms);
        updatedRooms
            .filter((r) => [RoomType.Sol, RoomType.Wall].includes(r.type!))
            .forEach((room) => {
                OpeningUtils.updateRoomOpenings(room, updatedOpenings);
                RoomItemUtils.updateRoomItems(room, updatedRoomItems);
                RoomDiffUtils.roomComputeDifference(room, updatedRoomItems, updatedWalls);
            });
        WallUtils.refreshWallsMinimumSizeAndOriginalAngle(updatedWalls, updatedOpenings);

        return {
            walls: updatedWalls,
            rooms: updatedRooms,
            openings: updatedOpenings,
            roomItems: updatedRoomItems,
        };
    };

    //#endregion

    public static getWalls(rooms?: Array<IRoom>): Wall[] {
        return rooms?.flatMap((x: IRoom) => x?.walls ?? []) ?? [];
    }
    public static getOpenings(rooms?: Array<IRoom>): IOpening[] {
        return rooms?.flatMap((x: IRoom) => x?.openings ?? []) ?? [];
    }
    public static getRoomItems(rooms?: Array<IRoom>): IRoomItem[] {
        return rooms?.flatMap((x: IRoom) => x?.roomItems ?? []) ?? [];
    }

    public static getRoomAllElements(rooms?: Array<IRoom>) {
        return {
            walls: MapHelper.getWalls(rooms),
            openings: MapHelper.getOpenings(rooms),
            roomItems: MapHelper.getRoomItems(rooms),
            rooms: rooms ?? [],
        };
    }
}
